import React, { useState, useRef, useEffect } from 'react'
import {
  Play, Settings, X, Plus, Zap, MessageSquare, Clock,
  CheckCircle, AlertCircle, Database
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

// Monotonic id counter so rapid adds within the same millisecond can't collide
let canvasIdSeq = 0
const nextCanvasId = (prefix: string) => `${prefix}-${Date.now()}-${++canvasIdSeq}`

// Types
type NodeValue = string | number | boolean | null | undefined | object
type NodeParams = Record<string, NodeValue>
type NodeInput = Record<string, NodeValue>
type NodeResultOutput = Record<string, NodeValue>

interface Node {
  id: string
  type: 'trigger' | 'action' | 'util'
  subtype: string
  label: string
  x: number
  y: number
  params: NodeParams
  isActive?: boolean
  hasError?: boolean
  result?: NodeResultOutput
  logs?: string[]
}

interface Edge {
  id: string
  from: string
  to: string
  label?: string // Para conexiones condicionales: 'true', 'false'
}

interface NodeContext {
  input: NodeInput
  timezone: string
}

interface NodeResult {
  output: NodeResultOutput
  logs?: string[]
  error?: string
}

interface NodeDef {
  type: string
  label: string
  icon: React.ReactNode
  color: string
  bgColor: string
  borderColor: string
  schema: {
    required: string[]
    properties: Record<string, unknown>
  }
  validate: (params: NodeParams) => string[]
  runMock: (params: NodeParams, ctx: NodeContext) => Promise<NodeResult>
  uiSpec: Array<{
    key: string
    label: string
    type: 'text' | 'number' | 'json' | 'textarea' | 'select'
    options?: string[]
    placeholder?: string
  }>
}

interface AutomationCanvasProps {
  automationName: string
  onClose: () => void
}

// Node Registry con colores de ActionsPage original
const NODE_REGISTRY: Record<string, NodeDef> = {
  'trigger.start': {
    type: 'trigger.start',
    label: 'Iniciar',
    icon: <Zap className="h-4 w-4" />,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary',
    schema: {
      required: [],
      properties: {}
    },
    validate: () => [],
    runMock: async () => {
      await new Promise(resolve => setTimeout(resolve, 500))
      return {
        output: {
          started: true,
          timestamp: new Date().toISOString()
        },
        logs: ['Flujo iniciado']
      }
    },
    uiSpec: []
  },
  'action.send_message': {
    type: 'action.send_message',
    label: 'Enviar Mensaje',
    icon: <MessageSquare className="h-4 w-4" />,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary',
    schema: {
      required: ['platform', 'content'],
      properties: {
        platform: { type: 'string' },
        content: { type: 'string' },
        phone: { type: 'string' },
        template: { type: 'string' },
        form_id: { type: 'string' },
        source: { type: 'string' }
      }
    },
    validate: (params) => {
      const errors: string[] = []
      if (!params.platform) errors.push('Plataforma es requerida')
      if (!params.content) errors.push('Contenido es requerido')
      if (params.platform === 'WhatsApp' && !params.phone) errors.push('Teléfono es requerido para WhatsApp')
      if (params.platform === 'Facebook' && !params.form_id) errors.push('Form ID es requerido para Facebook')
      if (params.platform === 'Domibus' && !params.source) errors.push('Source es requerido para Domibus')
      return errors
    },
    runMock: async (params) => {
      await new Promise(resolve => setTimeout(resolve, 800))

      let output: NodeResultOutput = { sent: true }
      let logs: string[] = []

      if (params.platform === 'WhatsApp') {
        output = {
          sent: true,
          platform: 'WhatsApp',
          phone: params.phone,
          template: params.template || 'text',
          preview: `Mensaje ${params.template || 'texto'} enviado a ${params.phone}`,
          messageId: `msg_${Date.now()}`
        }
        logs = [`WhatsApp enviado a ${params.phone}`]
      } else if (params.platform === 'Facebook') {
        output = {
          sent: true,
          platform: 'Facebook',
          form_id: params.form_id,
          content: params.content,
          preview: `Lead de Facebook procesado desde formulario ${params.form_id}`
        }
        logs = [`Lead de Facebook procesado desde formulario ${params.form_id}`]
      } else if (params.platform === 'Domibus') {
        output = {
          sent: true,
          platform: 'Domibus',
          source: params.source,
          content: params.content,
          preview: `Lead de Domibus procesado desde ${params.source}`
        }
        logs = [`Lead de Domibus procesado desde ${params.source}`]
      }

      return { output, logs }
    },
    uiSpec: [
      {
        key: 'platform',
        label: 'Plataforma',
        type: 'select',
        options: ['WhatsApp', 'Facebook', 'Domibus'],
        placeholder: 'Selecciona plataforma'
      },
      { key: 'content', label: 'Contenido', type: 'textarea', placeholder: 'Contenido del mensaje' },
      { key: 'phone', label: 'Teléfono (WhatsApp)', type: 'text', placeholder: '+57 3001234567' },
      { key: 'template', label: 'Template (WhatsApp)', type: 'text', placeholder: 'welcome_template' },
      { key: 'form_id', label: 'Form ID (Facebook)', type: 'text', placeholder: '123456789' },
      { key: 'source', label: 'Source (Domibus)', type: 'text', placeholder: 'domibus' }
    ]
  },
  'action.muveran_save': {
    type: 'action.muveran_save',
    label: 'Guardar en MUVERAN',
    icon: <Database className="h-4 w-4" />,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary',
    schema: {
      required: ['conversation_name', 'filter_id'],
      properties: {
        conversation_name: { type: 'string' },
        filter_id: { type: 'string' },
        message_id: { type: 'string' }
      }
    },
    validate: (params) => {
      const errors: string[] = []
      if (!params.conversation_name) errors.push('Nombre de conversación es requerido')
      if (!params.filter_id) errors.push('Filter ID es requerido')
      return errors
    },
    runMock: async (params) => {
      await new Promise(resolve => setTimeout(resolve, 600))
      return {
        output: {
          saved: true,
          location: `conversations/${params.filter_id}`,
          conversationId: `conv_${Date.now()}`,
          dashboard: 'Conversations'
        },
        logs: [`Guardado en conversations/${params.filter_id}`]
      }
    },
    uiSpec: [
      { key: 'conversation_name', label: 'Nombre de Conversación', type: 'text', placeholder: 'Lead Follow-up' },
      { key: 'filter_id', label: 'Filter ID', type: 'text', placeholder: 'filter_123' },
      { key: 'message_id', label: 'Message ID', type: 'text', placeholder: 'opcional' }
    ]
  },
  'util.delay': {
    type: 'util.delay',
    label: 'Esperar',
    icon: <Clock className="h-4 w-4" />,
    color: 'text-muted-foreground',
    bgColor: 'bg-muted/50',
    borderColor: 'border-border',
    schema: {
      required: ['ms'],
      properties: {
        ms: { type: 'number' }
      }
    },
    validate: (params) => {
      const errors: string[] = []
      if (!params.ms || (params.ms as number) <= 0) errors.push('Delay debe ser mayor a 0')
      return errors
    },
    runMock: async (params) => {
      await new Promise(resolve => setTimeout(resolve, Math.min(params.ms as number, 2000)))
      return {
        output: { waitedMs: params.ms },
        logs: [`Esperado ${params.ms}ms`]
      }
    },
    uiSpec: [
      { key: 'ms', label: 'Delay (milisegundos)', type: 'number', placeholder: '5000' }
    ]
  },
  'util.if': {
    type: 'util.if',
    label: 'Si/Entonces',
    icon: <Settings className="h-4 w-4" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500',
    schema: {
      required: ['condition'],
      properties: {
        condition: { type: 'string' },
        field: { type: 'string' },
        operator: { type: 'string' },
        value: { type: 'string' }
      }
    },
    validate: (params) => {
      const errors: string[] = []
      if (!params.condition) errors.push('Condición es requerida')
      return errors
    },
    runMock: async (params, ctx) => {
      await new Promise(resolve => setTimeout(resolve, 300))

      // Evaluar condición simple
      let result = false
      if (params.condition === 'has_phone') {
        result = ctx.input?.phone ? true : false
      } else if (params.condition === 'has_email') {
        result = ctx.input?.email ? true : false
      } else if (params.condition === 'source_domibus') {
        result = ctx.input?.source === 'domibus'
      } else if (params.condition === 'source_facebook') {
        result = ctx.input?.source === 'facebook'
      }

      return {
        output: {
          condition: params.condition,
          result,
          nextNode: result ? 'true' : 'false'
        },
        logs: [`Condición "${params.condition}" evaluada: ${result ? 'VERDADERO' : 'FALSO'}`]
      }
    },
    uiSpec: [
      {
        key: 'condition',
        label: 'Condición',
        type: 'select',
        options: ['has_phone', 'has_email', 'source_domibus', 'source_facebook'],
        placeholder: 'Selecciona condición'
      },
      { key: 'field', label: 'Campo (opcional)', type: 'text', placeholder: 'ej. lead.source' },
      { key: 'operator', label: 'Operador (opcional)', type: 'text', placeholder: 'ej. equals, contains' },
      { key: 'value', label: 'Valor (opcional)', type: 'text', placeholder: 'ej. domibus' }
    ]
  },
  'util.end': {
    type: 'util.end',
    label: 'Finalizar',
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-green-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500',
    schema: {
      required: [],
      properties: {}
    },
    validate: () => [],
    runMock: async () => {
      return {
        output: { done: true },
        logs: ['Flujo completado']
      }
    },
    uiSpec: []
  }
}

export function AutomationCanvas({ automationName, onClose }: AutomationCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null)

  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [isRunningSingle, setIsRunningSingle] = useState(false)
  const [showNodePalette, setShowNodePalette] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024)

  // Drag and drop state
  const [draggedNode, setDraggedNode] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null)
  const [tempEdge, setTempEdge] = useState<{ from: { x: number; y: number }; to: { x: number; y: number } } | null>(null)

  const selectedNode = nodes.find(n => n.id === selectedNodeId)
  const nodeDef = selectedNode ? NODE_REGISTRY[selectedNode.subtype] : null

  // Global event listeners for better mouse tracking
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging || !draggedNode) return

      const canvasRect = canvasRef.current?.getBoundingClientRect()
      if (!canvasRect) return

      // Movimiento completamente libre
      const newX = e.clientX - canvasRect.left - dragOffset.x
      const newY = e.clientY - canvasRect.top - dragOffset.y

      setNodes(prev => prev.map(node =>
        node.id === draggedNode
          ? {
            ...node,
            x: newX,
            y: newY
          }
          : node
      ))
    }

    const handleGlobalMouseUp = () => {
      setIsDragging(false)
      setDraggedNode(null)
    }

    if (isDragging) {
      document.addEventListener('pointermove', handleGlobalMouseMove)
      document.addEventListener('pointerup', handleGlobalMouseUp)
    }

    return () => {
      document.removeEventListener('pointermove', handleGlobalMouseMove)
      document.removeEventListener('pointerup', handleGlobalMouseUp)
    }
  }, [isDragging, draggedNode, dragOffset])

  const handleNodeClick = (nodeId: string) => {
    // Solo seleccionar si no estamos arrastrando
    if (!isDragging) {
      setSelectedNodeId(nodeId)
    }
  }

  // Drag and drop handlers
  const handleMouseDown = (e: React.MouseEvent, nodeId: string) => {
    // Solo iniciar drag si no es un punto de conexión
    if ((e.target as HTMLElement).classList.contains('connection-point')) {
      return
    }

    e.preventDefault()
    e.stopPropagation()

    const node = nodes.find(n => n.id === nodeId)
    if (!node) return

    setDraggedNode(nodeId)
    setIsDragging(true)

    // Calcular offset desde donde se hizo click
    const nodeElement = (e.target as HTMLElement).closest('.node-container')
    if (nodeElement) {
      const rect = nodeElement.getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      })
    }
  }

  const handleMouseMove = () => {
    // El movimiento se maneja con event listeners globales
  }

  const handleMouseUp = () => {
    // El mouse up se maneja con event listeners globales
  }

  // Connection handlers
  const handleConnectionStart = (e: React.PointerEvent, nodeId: string) => {
    e.preventDefault()
    e.stopPropagation()
    // Release implicit pointer capture (touch) so pointerup fires on the target dot under the finger
    try { (e.currentTarget as Element).releasePointerCapture(e.pointerId) } catch { /* mouse pointers aren't implicitly captured */ }

    const node = nodes.find(n => n.id === nodeId)
    if (!node) return

    setConnectingFrom(nodeId)

    // Calcular posición inicial del punto de conexión de salida
    const canvasRect = canvasRef.current?.getBoundingClientRect()
    if (canvasRect) {
      setTempEdge({
        from: {
          x: node.x + 256 + 2, // Punto de salida (lado derecho del nodo + 2px)
          y: node.y + 60        // Centro vertical del nodo
        },
        to: {
          x: e.clientX - canvasRect.left,
          y: e.clientY - canvasRect.top
        }
      })
    }
  }

  const handleConnectionEnd = (e: React.MouseEvent, targetNodeId: string) => {
    e.preventDefault()
    e.stopPropagation()

    if (!connectingFrom || connectingFrom === targetNodeId) {
      setConnectingFrom(null)
      setTempEdge(null)
      return
    }

    // Verificar que no existe ya esta conexión
    const existingEdge = edges.find(edge =>
      edge.from === connectingFrom && edge.to === targetNodeId
    )

    if (!existingEdge) {
      // Create new edge
      const newEdge: Edge = {
        id: `edge-${Date.now()}`,
        from: connectingFrom,
        to: targetNodeId
      }

      setEdges(prev => [...prev, newEdge])
    }

    setConnectingFrom(null)
    setTempEdge(null)
  }

  // Actualizar línea temporal mientras arrastras
  const handleConnectionMove = (e: React.MouseEvent) => {
    if (!connectingFrom || !tempEdge) return

    const canvasRect = canvasRef.current?.getBoundingClientRect()
    if (canvasRect) {
      setTempEdge(prev => prev ? {
        ...prev,
        to: {
          x: e.clientX - canvasRect.left,
          y: e.clientY - canvasRect.top
        }
      } : null)
    }
  }

  const handleCanvasClick = () => {
    if (connectingFrom) {
      setConnectingFrom(null)
      setTempEdge(null)
    }
  }

  const handleParamChange = (nodeId: string, key: string, value: NodeValue) => {
    setNodes(prev => prev.map(node =>
      node.id === nodeId
        ? { ...node, params: { ...node.params, [key]: value } }
        : node
    ))
  }

  const runFlow = async () => {
    setIsRunning(true)

    // Reset all nodes
    setNodes(prev => prev.map(node => ({ ...node, isActive: false, hasError: false, result: undefined, logs: [] })))

    // Get topological order with conditional logic
    const sortedNodes = getTopologicalOrderWithConditionals(nodes, edges)

    let currentInput = {
      name: 'Ana García',
      phone: '+57 3001234567',
      email: 'ana@example.com',
      source: 'domibus',
      service: 'SVC_A'
    }

    for (const node of sortedNodes) {
      const nodeDef = NODE_REGISTRY[node.subtype]
      if (!nodeDef) continue

      // Mark as active
      setNodes(prev => prev.map(n =>
        n.id === node.id ? { ...n, isActive: true } : n
      ))

      try {
        const result = await nodeDef.runMock(node.params, {
          input: currentInput,
          timezone: 'America/Bogota'
        })

        // Update with result
        setNodes(prev => prev.map(n =>
          n.id === node.id
            ? { ...n, isActive: false, result: result.output, hasError: false, logs: result.logs }
            : n
        ))

        // Handle conditional logic
        if (node.subtype === 'util.if' && result.output.nextNode) {
          // Skip nodes that don't match the condition
          const nextNodes = edges.filter(e => e.from === node.id)
          const trueNode = nextNodes.find(e => e.label === 'true')
          const falseNode = nextNodes.find(e => e.label === 'false')

          if (result.output.result && trueNode) {
            currentInput = { ...currentInput, ...result.output }
          } else if (!result.output.result && falseNode) {
            currentInput = { ...currentInput, ...result.output }
          }
        } else {
          currentInput = { ...currentInput, ...result.output }
        }
      } catch (error) {
        setNodes(prev => prev.map(n =>
          n.id === node.id
            ? { ...n, isActive: false, hasError: true, result: { error: (error as Error).message }, logs: [`Error: ${(error as Error).message}`] }
            : n
        ))
        break
      }
    }

    setIsRunning(false)
  }

  const runSingleNode = async (nodeId: string) => {
    setIsRunningSingle(true)

    const node = nodes.find(n => n.id === nodeId)
    if (!node) return

    const nodeDef = NODE_REGISTRY[node.subtype]
    if (!nodeDef) return

    // Mark as active
    setNodes(prev => prev.map(n =>
      n.id === nodeId ? { ...n, isActive: true } : n
    ))

    try {
      const result = await nodeDef.runMock(node.params, {
        input: {},
        timezone: 'America/Bogota'
      })

      setNodes(prev => prev.map(n =>
        n.id === nodeId
          ? { ...n, isActive: false, result: result.output, hasError: false, logs: result.logs }
          : n
      ))
    } catch (error) {
      setNodes(prev => prev.map(n =>
        n.id === nodeId
          ? { ...n, isActive: false, hasError: true, result: { error: (error as Error).message }, logs: [`Error: ${(error as Error).message}`] }
          : n
      ))
    }

    setIsRunningSingle(false)
  }

  const getTopologicalOrderWithConditionals = (nodes: Node[], edges: Edge[]): Node[] => {
    const visited = new Set<string>()
    const result: Node[] = []

    const visit = (nodeId: string, condition?: boolean) => {
      if (visited.has(nodeId)) return
      visited.add(nodeId)

      const node = nodes.find(n => n.id === nodeId)
      if (node) {
        result.push(node)
      }

      // Visit connected nodes
      const connectedEdges = edges.filter(e => e.from === nodeId)

      if (node?.subtype === 'util.if') {
        // For conditional nodes, visit based on condition
        const trueEdge = connectedEdges.find(e => e.label === 'true')
        const falseEdge = connectedEdges.find(e => e.label === 'false')

        if (condition === true && trueEdge) {
          visit(trueEdge.to, condition)
        } else if (condition === false && falseEdge) {
          visit(falseEdge.to, condition)
        }
      } else {
        // For regular nodes, visit all connected nodes
        connectedEdges.forEach(e => visit(e.to, condition))
      }
    }

    // Start with trigger nodes
    nodes
      .filter(n => n.type === 'trigger')
      .forEach(n => visit(n.id))

    return result
  }

  const deleteNode = (nodeId: string) => {
    setNodes(prev => prev.filter(n => n.id !== nodeId))
    deleteNodeConnections(nodeId) // Eliminar todas las conexiones del nodo
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null)
    }
  }

  // const deleteEdge = (edgeId: string) => {
  //   setEdges(prev => prev.filter(e => e.id !== edgeId))
  // }

  // Función para eliminar todas las conexiones de un nodo
  const deleteNodeConnections = (nodeId: string) => {
    setEdges(prev => prev.filter(e => e.from !== nodeId && e.to !== nodeId))
  }

  const addNode = (subtype: string) => {
    const nodeDef = NODE_REGISTRY[subtype]
    if (!nodeDef) return

    const newNode: Node = {
      id: nextCanvasId('node'),
      type: subtype.startsWith('trigger') ? 'trigger' : subtype.startsWith('action') ? 'action' : 'util',
      subtype,
      label: nodeDef.label,
      x: 300 + Math.random() * 400,
      y: 200 + Math.random() * 300,
      params: {}
    }

    setNodes(prev => [...prev, newNode])
  }

  return (
    <div className="flex h-full bg-background">
      {/* Node Palette - Left Sidebar */}
      {showNodePalette && (
        <div className="w-64 bg-background border-r border-border flex flex-col max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-40 max-lg:w-72 max-lg:shadow-2xl">
          <div className="p-4 border-b border-border flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold">Nodos</h3>
              <p className="text-sm text-muted-foreground">Toca para agregar</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 lg:hidden" onClick={() => setShowNodePalette(false)} aria-label="Cerrar">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Triggers */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Triggers</h4>
              <div className="space-y-2">
                {Object.entries(NODE_REGISTRY)
                  .filter(([, def]) => def.type.startsWith('trigger'))
                  .map(([key, def]) => (
                    <Button
                      key={key}
                      onClick={() => addNode(key)}
                      variant="outline"
                      className="w-full justify-start h-auto p-3 hover:bg-accent"
                    >
                      <div className={`p-1 rounded ${def.bgColor} ${def.borderColor} border mr-3`}>
                        <div className={def.color}>
                          {def.icon}
                        </div>
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-sm">{def.label}</div>
                        <div className="text-xs text-muted-foreground">{def.type}</div>
                      </div>
                    </Button>
                  ))}
              </div>
            </div>

            {/* Actions */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Acciones</h4>
              <div className="space-y-2">
                {Object.entries(NODE_REGISTRY)
                  .filter(([, def]) => def.type.startsWith('action'))
                  .map(([key, def]) => (
                    <Button
                      key={key}
                      onClick={() => addNode(key)}
                      variant="outline"
                      className="w-full justify-start h-auto p-3 hover:bg-accent"
                    >
                      <div className={`p-1 rounded ${def.bgColor} ${def.borderColor} border mr-3`}>
                        <div className={def.color}>
                          {def.icon}
                        </div>
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-sm">{def.label}</div>
                        <div className="text-xs text-muted-foreground">{def.type}</div>
                      </div>
                    </Button>
                  ))}
              </div>
            </div>

            {/* Nodes */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Nodos</h4>
              <div className="space-y-2">
                {Object.entries(NODE_REGISTRY)
                  .map(([key, def]) => (
                    <Button
                      key={key}
                      onClick={() => addNode(key)}
                      variant="outline"
                      className="w-full justify-start h-auto p-3 hover:bg-accent"
                    >
                      <div className={`p-1 rounded ${def.bgColor} ${def.borderColor} border mr-3`}>
                        <div className={def.color}>
                          {def.icon}
                        </div>
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-sm">{def.label}</div>
                        <div className="text-xs text-muted-foreground">{def.type}</div>
                      </div>
                    </Button>
                  ))}
              </div>
            </div>

            {/* Utils */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Utilidades</h4>
              <div className="space-y-2">
                {Object.entries(NODE_REGISTRY)
                  .filter(([, def]) => def.type.startsWith('util'))
                  .map(([key, def]) => (
                    <Button
                      key={key}
                      onClick={() => addNode(key)}
                      variant="outline"
                      className="w-full justify-start h-auto p-3 hover:bg-accent"
                    >
                      <div className={`p-1 rounded ${def.bgColor} ${def.borderColor} border mr-3`}>
                        <div className={def.color}>
                          {def.icon}
                        </div>
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-sm">{def.label}</div>
                        <div className="text-xs text-muted-foreground">{def.type}</div>
                      </div>
                    </Button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div className="flex-1 relative overflow-auto" ref={canvasRef}>
        {/* Top Toolbar */}
        <div className="absolute top-4 left-4 z-10 flex gap-2">
          <Button onClick={runFlow} disabled={isRunning} size="sm" className="bg-primary hover:bg-primary/90">
            <Play className="mr-2 h-4 w-4" />
            {isRunning ? 'Ejecutando...' : 'Probar Flujo'}
          </Button>
          <Button
            onClick={() => setShowNodePalette(!showNodePalette)}
            variant="outline"
            size="sm"
          >
            <Settings className="mr-2 h-4 w-4" />
            {showNodePalette ? 'Ocultar' : 'Mostrar'} Nodos
          </Button>
          <Button
            onClick={() => {
              // Add a conditional flow example
              const ifNode = {
                id: nextCanvasId('if'),
                type: 'util' as const,
                subtype: 'util.if',
                label: 'Si/Entonces',
                x: 400,
                y: 300,
                params: { condition: 'has_phone' }
              }
              setNodes(prev => [...prev, ifNode])
            }}
            variant="outline"
            size="sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Agregar Condicional
          </Button>
        </div>

        {/* Canvas Grid */}
        <div
          className="relative w-full h-full bg-background"
          style={{
            backgroundImage: `
              radial-gradient(circle, hsl(var(--muted)) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
            minHeight: '200vh',
            minWidth: '200vw'
          }}
          onPointerMove={(e) => {
            handleMouseMove()
            handleConnectionMove(e)
          }}
          onPointerUp={handleMouseUp}
          onPointerLeave={handleMouseUp}
          onClick={handleCanvasClick}
        >
          {/* Nodes */}
          <div className="relative w-full h-full">
            {nodes.map(node => {
              const def = NODE_REGISTRY[node.subtype]
              return (
                <div
                  key={node.id}
                  className={`absolute node-container transition-shadow duration-200 ${selectedNodeId === node.id ? 'ring-2 ring-primary ring-offset-2' : ''
                    } ${node.isActive ? 'ring-2 ring-green-500 ring-offset-2 animate-pulse' : ''} ${node.hasError ? 'ring-2 ring-red-500 ring-offset-2' : ''
                    } ${isDragging && draggedNode === node.id ? 'z-50 cursor-grabbing' : 'cursor-grab'}`}
                  style={{ left: node.x, top: node.y, touchAction: 'none' }}
                  onClick={() => handleNodeClick(node.id)}
                  onPointerDown={(e) => handleMouseDown(e, node.id)}
                >
                  <Card className={`w-64 ${def?.bgColor} ${def?.borderColor} border-2 shadow-lg hover:shadow-xl transition-shadow ${isDragging && draggedNode === node.id ? 'shadow-2xl scale-105' : ''
                    }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${def?.bgColor} ${def?.borderColor} border`}>
                          <div className={def?.color}>
                            {def?.icon}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{node.label}</h3>
                          <p className="text-xs text-muted-foreground">{node.subtype}</p>
                        </div>
                        <div className="text-xs text-muted-foreground opacity-50 cursor-grab">
                          ⋮⋮
                        </div>
                      </div>

                      {/* Status Indicators */}
                      <div className="space-y-2">
                        {node.result && !node.hasError && (
                          <div className="flex items-center gap-2 text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded p-2">
                            <CheckCircle className="h-3 w-3" />
                            <span>✓ Ejecutado</span>
                          </div>
                        )}
                        {node.hasError && (
                          <div className="flex items-center gap-2 text-xs bg-destructive/10 text-destructive rounded p-2">
                            <AlertCircle className="h-3 w-3" />
                            <span>✗ Error</span>
                          </div>
                        )}
                        {node.logs && node.logs.length > 0 && (
                          <div className="text-xs bg-muted text-muted-foreground rounded p-2">
                            {node.logs[node.logs.length - 1]}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Connection points */}
                  <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 z-20">
                    <div
                      className="connection-point touch-none w-4 h-4 bg-blue-500 rounded-full border-2 border-white cursor-crosshair hover:bg-blue-600 hover:scale-125 transition-all duration-200 shadow-md"
                      onPointerDown={(e) => handleConnectionStart(e, node.id)}
                      title="Arrastra para conectar"
                    />
                  </div>
                  <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 z-20">
                    <div
                      className="connection-point touch-none w-4 h-4 bg-gray-400 rounded-full border-2 border-white cursor-crosshair hover:bg-blue-500 hover:scale-125 transition-all duration-200 shadow-md"
                      onPointerUp={(e) => handleConnectionEnd(e, node.id)}
                      title="Suelta para conectar"
                    />
                  </div>
                </div>
              )
            })}

            {/* Edges */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {edges.map(edge => {
                const fromNode = nodes.find(n => n.id === edge.from)
                const toNode = nodes.find(n => n.id === edge.to)
                if (!fromNode || !toNode) return null

                // Conectar exactamente a los puntos de conexión
                const startX = fromNode.x + 256 + 2 // Punto de salida (lado derecho del nodo + 2px)
                const startY = fromNode.y + 60 // Centro vertical del nodo
                const endX = toNode.x - 2 // Punto de entrada (lado izquierdo del nodo - 2px)
                const endY = toNode.y + 60 // Centro vertical del nodo

                // Calcular punto de control para curva suave
                const controlX = startX + (endX - startX) * 0.5
                const controlY = startY + (endY - startY) * 0.2

                // Diferentes colores para conexiones condicionales
                const strokeColor = edge.label === 'true' ? '#10b981' :
                  edge.label === 'false' ? '#ef4444' :
                    '#6b7280'

                return (
                  <g key={edge.id}>
                    {/* Línea principal - sin flecha */}
                    <path
                      d={`M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`}
                      stroke={strokeColor}
                      strokeWidth="2"
                      fill="none"
                      className="transition-all duration-300"
                    />
                    {/* Etiqueta para conexiones condicionales */}
                    {edge.label && (
                      <text
                        x={controlX}
                        y={controlY - 10}
                        textAnchor="middle"
                        className="text-xs font-medium fill-current"
                        style={{ color: strokeColor }}
                      >
                        {edge.label === 'true' ? 'SÍ' : edge.label === 'false' ? 'NO' : edge.label}
                      </text>
                    )}
                  </g>
                )
              })}

              {/* Línea temporal mientras conectas */}
              {tempEdge && (
                <path
                  d={`M ${tempEdge.from.x} ${tempEdge.from.y} L ${tempEdge.to.x} ${tempEdge.to.y}`}
                  stroke="#6b7280"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  fill="none"
                  className="animate-pulse"
                />
              )}
            </svg>
          </div>
        </div>
      </div>

      {/* Inspector Panel - Right Sidebar */}
      <div className={`w-80 bg-background border-l border-border flex flex-col ${selectedNode ? 'max-lg:fixed max-lg:inset-y-0 max-lg:right-0 max-lg:z-40 max-lg:w-[85vw] max-lg:max-w-sm max-lg:shadow-2xl' : 'max-lg:hidden'}`}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Inspector</h3>
              <p className="text-sm text-muted-foreground">{automationName}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setSelectedNodeId(null)} aria-label="Cerrar inspector">
                <X className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="hidden lg:inline-flex" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {selectedNode && nodeDef ? (
            <div className="space-y-6">
              {/* Node Info */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${nodeDef.bgColor} ${nodeDef.borderColor} border`}>
                    <div className={nodeDef.color}>
                      {nodeDef.icon}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold">{nodeDef.label}</h4>
                    <Badge variant="outline" className="text-xs">{selectedNode.subtype}</Badge>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Parameters */}
              <div>
                <h5 className="font-medium mb-3">Parámetros</h5>
                <div className="space-y-4">
                  {nodeDef.uiSpec.map(spec => (
                    <div key={spec.key}>
                      <Label className="text-sm font-medium text-muted-foreground">{spec.label}</Label>
                      {spec.type === 'text' && (
                        <Input
                          value={(selectedNode.params[spec.key] as string) || ''}
                          onChange={(e) => handleParamChange(selectedNode.id, spec.key, e.target.value)}
                          className="mt-1"
                          placeholder={spec.placeholder}
                        />
                      )}
                      {spec.type === 'number' && (
                        <Input
                          type="number"
                          value={(selectedNode.params[spec.key] as number) || ''}
                          onChange={(e) => handleParamChange(selectedNode.id, spec.key, parseInt(e.target.value))}
                          className="mt-1"
                          placeholder={spec.placeholder}
                        />
                      )}
                      {spec.type === 'textarea' && (
                        <Textarea
                          value={(selectedNode.params[spec.key] as string) || ''}
                          onChange={(e) => handleParamChange(selectedNode.id, spec.key, e.target.value)}
                          className="mt-1"
                          rows={3}
                          placeholder={spec.placeholder}
                        />
                      )}
                      {spec.type === 'select' && (
                        <Select
                          value={(selectedNode.params[spec.key] as string) || ''}
                          onValueChange={(value) => handleParamChange(selectedNode.id, spec.key, value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder={spec.placeholder} />
                          </SelectTrigger>
                          <SelectContent>
                            {spec.options?.map(option => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Test Actions */}
              <div>
                <h5 className="font-medium mb-3">Pruebas</h5>
                <div className="space-y-2">
                  <Button
                    onClick={() => runSingleNode(selectedNode.id)}
                    disabled={isRunningSingle}
                    size="sm"
                    className="w-full"
                    variant="outline"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    {isRunningSingle ? 'Probando...' : 'Probar este nodo'}
                  </Button>
                </div>
              </div>

              {/* Results */}
              {selectedNode.result && (
                <>
                  <Separator />
                  <div>
                    <h5 className="font-medium mb-3">Resultado</h5>
                    <div className="bg-muted/50 rounded-lg p-3 text-sm">
                      <pre className="whitespace-pre-wrap text-muted-foreground">
                        {JSON.stringify(selectedNode.result, null, 2)}
                      </pre>
                    </div>
                  </div>
                </>
              )}

              {/* Logs */}
              {selectedNode.logs && selectedNode.logs.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h5 className="font-medium mb-3">Logs</h5>
                    <div className="space-y-1">
                      {selectedNode.logs.map((log, i) => (
                        <div key={i} className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                          {log}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Validation Errors */}
              {nodeDef.validate(selectedNode.params).length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h5 className="font-medium text-red-600 mb-3">Errores de Validación</h5>
                    <div className="bg-red-500/10 rounded-lg p-3 text-sm text-red-600">
                      {nodeDef.validate(selectedNode.params).map((error, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <AlertCircle className="h-3 w-3" />
                          <span>{error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Connections Info */}
              {(() => {
                const incomingEdges = edges.filter(e => e.to === selectedNode.id)
                const outgoingEdges = edges.filter(e => e.from === selectedNode.id)

                if (incomingEdges.length > 0 || outgoingEdges.length > 0) {
                  return (
                    <>
                      <Separator />
                      <div>
                        <h5 className="font-medium mb-3">Conexiones</h5>
                        <div className="space-y-2 text-sm">
                          {incomingEdges.length > 0 && (
                            <div className="text-blue-600">
                              ← {incomingEdges.length} entrada{incomingEdges.length > 1 ? 's' : ''}
                            </div>
                          )}
                          {outgoingEdges.length > 0 && (
                            <div className="text-green-600">
                              → {outgoingEdges.length} salida{outgoingEdges.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )
                }
                return null
              })()}

              {/* Actions */}
              <Separator />
              <div className="space-y-2">
                <Button
                  onClick={() => deleteNode(selectedNode.id)}
                  variant="destructive"
                  size="sm"
                  className="w-full"
                >
                  <X className="mr-2 h-4 w-4" />
                  Eliminar Nodo
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-12">
              <Settings className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Selecciona un nodo</p>
              <p className="text-sm">para ver sus parámetros y configuraciones</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}